import { Flex, FormControl, FormHelperText, FormLabel, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useSetResponsiblePersonMutation, type PersonDto } from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { coordinatorRegistry } from "../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../common/errors/useApiError";
import { PersonSelector } from "../../../../../common/persons/PersonSelector";
import { useSub } from "../../../../../common/subscription/useSub";
import { CREATE_EVENT_ROLES } from "../../../../v2/sourcing-event/ChangeResponsiblePersonButton";

interface Props {
  eventId: string;
  responsiblePerson: PersonDto;
}

const personName = (p: PersonDto) => [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;

export const ResponsibleBuyerField = ({ eventId, responsiblePerson }: Props) => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { canCreateEvents } = useSub();
  const { trigger } = useApiError();
  const [setResponsible, { isLoading }] = useSetResponsiblePersonMutation();

  const apply = async (person: PersonDto | null) => {
    if (!person || person.id === responsiblePerson.id) return;
    try {
      await coordinatorRegistry.flush(eventId);
      const updated = await setResponsible({
        eventId,
        setResponsiblePersonRequest: { responsiblePersonId: person.id },
      }).unwrap();
      coordinatorRegistry.get(eventId)?.setServerState(updated);
    } catch (e) {
      trigger(e);
    }
  };

  if (!canCreateEvents) {
    return (
      <FormControl mb="2">
        <FormLabel mb="0">{t("Responsible buyer")}</FormLabel>
        <Text fontWeight="semibold">{personName(responsiblePerson)}</Text>
      </FormControl>
    );
  }

  return (
    <FormControl mb="2">
      <Flex align="center" gap="1">
        <FormLabel mb="0">{t("Responsible buyer")}</FormLabel>
        {isLoading && <Spinner size="xs" />}
      </Flex>
      <PersonSelector
        size="md"
        organizationIds={[selectedOrg.id]}
        roles={CREATE_EVENT_ROLES}
        selectedPerson={responsiblePerson}
        onChange={apply}
        isClearable={false}
        isDisabled={isLoading}
      />
      <FormHelperText>{t("Only users who can create procurements are listed")}</FormHelperText>
    </FormControl>
  );
};
